import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Home, Search, HelpCircle, Mail, X, Download } from "lucide-react";

import { trackEvent } from "../lib/analytics";

const isNativeApp = () => {
  return (
    window.Capacitor?.isNativePlatform?.() === true ||
    window.location.protocol === "capacitor:"
  );
};

export default function MobileBottomNav() {
  const [showInstall, setShowInstall] = useState(false);
  const [deviceType, setDeviceType] = useState("");
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    if (isNativeApp()) {
      setShowInstall(false);
      return;
    }

    const ua = navigator.userAgent || "";
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isStandalone =
      window.navigator.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches;

    if (isStandalone) {
      setShowInstall(false);
      return;
    }

    if (isAndroid) {
      setDeviceType("android");
      setShowInstall(true);
      trackEvent("install_banner_shown", { device_type: "android" });
    }

    if (isIOS) {
      setDeviceType("ios");
      setShowInstall(true);
      trackEvent("install_banner_shown", { device_type: "ios" });
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      window.deferredPrompt = e;
      setDeviceType("android");
      setShowInstall(true);
      trackEvent("pwa_install_prompt_available", { device_type: "android" });
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, []);

  const dismissInstall = () => {
    setShowInstall(false);
    trackEvent("install_banner_dismissed", { device_type: deviceType });
  };

  const handleInstallClick = async () => {
    trackEvent("install_banner_clicked", { device_type: deviceType });

    const promptEvent = deferredPrompt || window.deferredPrompt;

    if (deviceType === "android") {
      if (promptEvent) {
        promptEvent.prompt();

        const choice = await promptEvent.userChoice;

        trackEvent("pwa_install_choice", {
          outcome: choice?.outcome || "unknown",
        });

        setDeferredPrompt(null);
        window.deferredPrompt = null;
        setShowInstall(false);
        return;
      }

      alert(
        "To install PinMyDoc, open your browser menu and choose Install app or Add to Home screen."
      );
      return;
    }

    if (deviceType === "ios") {
      alert(
        "To install PinMyDoc on iPhone: open this website in Safari, tap Share, then tap Add to Home Screen."
      );
    }
  };

  const navClass = ({ isActive }) =>
    `flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-all ${
      isActive ? "text-[#356DFF]" : "text-slate-500"
    }`;

  return (
    <>
      {showInstall && (
        <div
          className="md:hidden fixed left-3 right-3 z-[60] rounded-2xl border border-slate-200 bg-white/95 backdrop-blur-xl shadow-2xl px-4 py-3"
          style={{
            bottom: "calc(82px + env(safe-area-inset-bottom))",
          }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#EEF5FB] flex items-center justify-center shrink-0">
              <Download className="w-5 h-5 text-[#356DFF]" />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-900">
                Install PinMyDoc
              </p>

              <p className="text-xs text-slate-500 mt-0.5">
                {deviceType === "android"
                  ? "Install the PWA app on your phone."
                  : "Use Safari → Share → Add to Home Screen."}
              </p>
            </div>

            <button
              type="button"
              onClick={handleInstallClick}
              className="rounded-xl bg-[#356DFF] text-white px-3 py-2 text-xs font-bold"
            >
              Install
            </button>

            <button
              type="button"
              onClick={dismissInstall}
              className="text-slate-400 hover:text-slate-700"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      <div
        className="mobile-bottom-nav md:hidden fixed left-0 right-0 z-50 border-t border-slate-200 bg-white/95 backdrop-blur-xl shadow-[0_-8px_30px_rgba(0,0,0,0.06)]"
        style={{
          bottom: 0,
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <div className="grid grid-cols-4 h-[64px]">
          <NavLink to="/" className={navClass}>
            <Home size={21} />
            <span>Home</span>
          </NavLink>

          <NavLink to="/doctors" className={navClass}>
            <Search size={21} />
            <span>Find Care</span>
          </NavLink>

          <NavLink to="/faq" className={navClass}>
            <HelpCircle size={21} />
            <span>FAQ</span>
          </NavLink>

          <a
            href="mailto:support@techdroid.ca?subject=PinMyDoc%20Support"
            onClick={() => trackEvent("support_email_clicked")}
            className="flex flex-col items-center justify-center gap-1 text-[11px] font-medium text-slate-500"
          >
            <Mail size={21} />
            <span>Support</span>
          </a>
        </div>
      </div>
    </>
  );
}